import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Chip, Stack, Pagination, TableSortLabel,
} from '@mui/material';
import { currency, fmtDate, clientIdLabel } from './format';

// Generic table for funding / commission rows. When showClient is true a
// client column is shown (used on the unified ledger view).
export default function FundingTable({
  rows = [], showClient = false, sortBy, order = 'desc', onSort,
  total, page, pages, onPageChange, sumAmount, amountLabel = 'Amount',
  emptyText = 'No records found',
}) {
  const cols = [
    { id: 'date', label: 'Date', sortable: true },
    ...(showClient ? [{ id: 'client_id', label: 'Client', sortable: true }] : []),
    { id: 'lender', label: 'Lender', sortable: true },
    { id: 'amount', label: amountLabel, sortable: true, align: 'right' },
    { id: 'deposit_id', label: 'Deposit ID', sortable: false },
  ];

  return (
    <Box>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map((c) => (
                <TableCell key={c.id} align={c.align || 'left'} sx={{ fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                  {c.sortable && onSort ? (
                    <TableSortLabel
                      active={sortBy === c.id}
                      direction={sortBy === c.id ? order : 'asc'}
                      onClick={() => onSort(c.id)}
                    >
                      {c.label}
                    </TableSortLabel>
                  ) : c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r._id || `${r.deposit_id}-${r.client_id}`} hover>
                <TableCell>{fmtDate(r.date)}</TableCell>
                {showClient && (
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }} title={r.client_id}>
                      {clientIdLabel(r.client_id)}
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  <Chip label={r.lender || 'Unknown'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700}>{currency(r.amount)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{r.deposit_id}</Typography>
                  {r.source === 'unmatched_match' && (
                    <Chip label="matched" size="small" color="info" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={cols.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>{emptyText}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {total != null ? `${total} record${total === 1 ? '' : 's'}` : `${rows.length} records`}
          {sumAmount != null ? ` · total ${currency(sumAmount)}` : ''}
        </Typography>
        {pages > 1 && <Pagination count={pages} page={page} onChange={(_, p) => onPageChange?.(p)} size="small" />}
      </Stack>
    </Box>
  );
}
